import { useState } from 'react';
import { Icon, Button } from '@/components/atoms';

interface DateRangeFilter {
  startDate: string;
  endDate: string;
}

interface ReportFiltersProps {
  showDateRange?: boolean;
  showWarehouse?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  warehouses?: Array<{ id: string; name: string }>;
  onFilterChange: (filters: Record<string, unknown>) => void;
  onExport: (format: 'xlsx' | 'pdf') => void;
  isExporting?: boolean;
}

export function ReportFilters({
  showDateRange = true,
  showWarehouse = false,
  showSearch = true,
  searchPlaceholder = 'Search...',
  warehouses = [],
  onFilterChange,
  onExport,
  isExporting = false,
}: ReportFiltersProps) {
  const [dateRange, setDateRange] = useState<DateRangeFilter>({
    startDate: '',
    endDate: '',
  });
  const [search, setSearch] = useState('');
  const [warehouseId, setWarehouseId] = useState('');

  const handleApplyFilters = () => {
    const filters: Record<string, unknown> = {};
    if (dateRange.startDate) filters.start_date = dateRange.startDate;
    if (dateRange.endDate) filters.end_date = dateRange.endDate;
    if (search) filters.search = search;
    if (warehouseId) filters.warehouse_id = warehouseId;
    onFilterChange(filters);
  };

  const handleClearFilters = () => {
    setDateRange({ startDate: '', endDate: '' });
    setSearch('');
    setWarehouseId('');
    onFilterChange({});
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
      <div className="flex flex-col lg:flex-row lg:items-end gap-4">
        {/* Search */}
        {showSearch && (
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Search</label>
            <div className="relative">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        )}

        {/* Date Range */}
        {showDateRange && (
          <>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </>
        )}

        {/* Warehouse Filter */}
        {showWarehouse && warehouses.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Warehouse</label>
            <select
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[150px]"
            >
              <option value="">All Warehouses</option>
              {warehouses.map((wh) => (
                <option key={wh.id} value={wh.id}>
                  {wh.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button variant="primary" size="sm" onClick={handleApplyFilters}>
            <Icon name="SlidersHorizontal" size={16} className="mr-1" />
            Apply
          </Button>
          <Button variant="secondary" size="sm" onClick={handleClearFilters}>
            Clear
          </Button>
        </div>

        {/* Export Buttons */}
        <div className="flex gap-2 lg:ml-auto">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onExport('xlsx')}
            disabled={isExporting}
          >
            <Icon name="FileSpreadsheet" size={16} className="mr-1" />
            Excel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onExport('pdf')}
            disabled={isExporting}
          >
            <Icon name="FileText" size={16} className="mr-1" />
            PDF
          </Button>
        </div>
      </div>
    </div>
  );
}
